import styled, { css } from "styled-components";

interface StyledTextFieldProps {
  $size: "large" | "medium" | "small";
  $width: string;
  $readOnly: boolean;
  $disabled: boolean;
  $success: boolean;
  $warning: boolean;
  $error: boolean;
}

export const StyledTextField = styled.div<StyledTextFieldProps>`
  display: flex;
  flex-flow: column nowrap;
  gap: 0.4rem;
  width: ${({ $width }) => $width};

  & > .label-area {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    gap: 0.4rem;
    width: max-content;
    font: ${({ $size }) =>
      $size === "large" ? "var(--label-medium-400)" : "var(--label-small-400)"};
    color: var(--text-gray-strong);
    & > .required {
      color: var(--sys-red-0);
    }
    & > .info {
      cursor: pointer;
      position: relative;
    }
  }

  & > .form-area {
    transition: all 0.1s ease;
    box-sizing: border-box;
    border-radius: 0.4rem;
    background-color: var(--bg-gray-normal);
    border: 0.1rem solid var(--bg-gray-2);
    display: flex;
    flex-flow: row nowrap;
    gap: 0.4rem;
    justify-content: start;
    align-items: center;
    width: 100%;
    height: ${({ $size }) =>
      $size === "large" ? "4rem" : $size === "medium" ? "2.8rem" : "2rem"};
    padding: ${({ $size }) => ($size === "large" ? "0.8rem" : "0.4rem 0.8rem")};
    &.animation {
      position: relative;
      animation: error-animation 0.5s ease;
    }
    & > .icon {
      ${({ $size }) =>
        $size === "large"
          ? css`
              width: 2rem;
              height: 2rem;
            `
          : $size === "medium"
          ? css`
              width: 1.6rem;
              height: 1.6rem;
            `
          : css`
              width: 1.2rem;
              height: 1.2rem;
            `};
      path {
        stroke: var(--text-gray-strong);
      }
    }

    & > input {
      width: 100%;
      outline: none;
      border: none;
      padding: 0;
      background-color: transparent;
      flex: 1;
      font: ${({ $size }) =>
        $size === "large"
          ? "var(--body-medium-400)"
          : $size === "medium"
          ? "var(--body-small-400)"
          : "var(--label-small-400)"};
      color: var(--text-gray-strong);
      &::placeholder {
        color: var(--text-gray-4);
      }
    }

    & > .del-btn {
      display: none;
    }

    & > .eye-btn {
      box-sizing: border-box;
      display: flex;
      width: fit-content;
      height: fit-content;
      background-color: transparent;
      border: none;
      border-radius: 0.2rem;
      padding: ${({ $size }) => ($size === "small" ? "0" : "0.2rem")};
      cursor: pointer;
      &:hover {
        background-color: var(--hovered);
      }
      &:active {
        background-color: var(--pressed-strong);
      }
      & > .icon {
        ${({ $size }) =>
          $size === "large"
            ? css`
                width: 2rem;
                height: 2rem;
              `
            : $size === "medium"
            ? css`
                width: 1.6rem;
                height: 1.6rem;
              `
            : css`
                width: 1.2rem;
                height: 1.2rem;
              `};
        path {
          stroke: var(--text-gray-strong);
          stroke-width: 1.6;
        }
      }
    }

    &:hover {
      background-color: var(--hovered);
      border: 0.1rem solid var(--bg-gray-2);
      & > .del-btn {
        display: flex;
      }
    }
    &:focus-within {
      background-color: var(--bg-gray-normal);
      border: 0.1rem solid var(--bg-gray-strong);
      & > .del-btn {
        display: flex;
      }
    }
  }

  & > .info-area {
    display: flex;
    gap: 0.8rem;
    flex-flow: row nowrap;
    color: var(--text-gray-4);
    font: ${({ $size }) =>
      $size === "large" ? "var(--label-medium-400)" : "var(--label-small-400)"};
    & > .help {
      flex: 1;
    }
    & > .count b {
      font: ${({ $size }) =>
        $size === "large"
          ? "var(--label-medium-600)"
          : "var(--label-small-600)"};
    }
  }

  ${({ $success }) =>
    $success &&
    css`
      & > .label-area {
        color: var(--pri-blue-5);
      }
      & > .form-area {
        border: 0.1rem solid var(--pri-blue-2) !important;
        background-color: var(--bg-gray-normal);
        & > input {
          color: var(--text-gray-strong);
        }
        & > .icon path {
          stroke: var(--pri-blue-5);
        }
      }
      & > .info-area {
        color: var(--pri-blue-5);
      }
    `}
  ${({ $warning }) =>
    $warning &&
    css`
      & > .label-area {
        color: var(--sec-orange-5);
      }
      & > .form-area {
        border: 0.1rem solid var(--sec-orange-2) !important;
        background-color: var(--bg-gray-normal);
        & > input {
          color: var(--text-gray-strong);
        }
        & > .icon path {
          stroke: var(--sec-orange-5);
        }
      }
      & > .info-area {
        color: var(--sec-orange-5);
      }
    `}
  ${({ $error }) =>
    $error &&
    css`
      & > .label-area {
        color: var(--sys-red-1);
      }
      & > .form-area {
        border: 0.1rem solid var(--sys-red-0) !important;
        background-color: var(--bg-gray-normal);
        & > input {
          color: var(--text-gray-strong);
        }
        & > .icon path {
          stroke: var(--sys-red-1);
        }
      }
      & > .info-area {
        color: var(--sys-red-1);
      }
    `}
  ${({ $readOnly }) =>
    $readOnly &&
    css`
      & > .form-area {
        border: 0.1rem solid var(--bg-gray-1);
        background-color: var(--bg-gray-0);
        color: var(--text-gray-strong);
        &::placeholder {
          color: transparent;
        }
        &:hover {
          border: 0.1rem solid var(--bg-gray-1);
          background-color: var(--bg-gray-0);
          color: var(--text-gray-strong);
        }
        &:focus-within {
          border: 0.1rem solid var(--bg-gray-1);
          background-color: var(--bg-gray-0);
          color: var(--text-gray-strong);
        }
      }
    `}
  ${({ $disabled }) =>
    $disabled &&
    css`
      pointer-events: none;
      & > .label-area {
        color: var(--bg-gray-2);
      }
      & > .form-area {
        border: 0.1rem solid var(--bg-gray-1);
        background-color: var(--bg-gray-0);
        & > input {
          color: var(--bg-gray-2);
          &::placeholder {
            color: transparent;
          }
        }
        & > .icon path {
          stroke: var(--bg-gray-2);
        }
      }
      & > .info-area {
        color: var(--bg-gray-2);
      }
    `}

    @keyframes error-animation {
    0% {
      left: -0.6rem;
    }
    10% {
      left: 0.5rem;
    }
    20% {
      left: -0.4rem;
    }
    40% {
      left: 0.3rem;
    }
    60% {
      left: -0.2rem;
    }
    80% {
      left: 0.1rem;
    }
    100% {
      left: 0;
    }
  }
`;
